import { type GraphQLSingularResponse, type RequestParameters, type PayloadError } from 'relay-runtime';

import { FetchError, handleGraphQlErrors } from '@atlassian/help-center-common-util/fetch/fetch';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { isFilterNullTraceIdFromGraphqlCallsEnabled } from '@helpcenter/feature-flags';
import { getEnv, EnvType } from '../../env';
import { getAggHeaders } from '../agg-headers';

const setEndpoint = (endpoint: string) => {
    const isJsmScaleTestAggCheckEnabled = FeatureGates.checkGate('jsm_scale_60k_testing_in_help_center');

    if (
        isJsmScaleTestAggCheckEnabled &&
        getEnv().environment !== EnvType.PROD &&
        typeof process.env.AGG_BRANCH_SLUG === 'string' &&
        process.env.AGG_BRANCH_SLUG.length > 0
    ) {
        return endpoint.replace(process.env.AGG_BRANCH_SLUG, 'branch-issue-remove-rate-limit');
    }

    return endpoint;
};

const getHeaders = () => {
    if (!FeatureGates.checkGate('jsm-help-center-agg-attribution-headers')) {
        return {};
    }

    return getAggHeaders();
};

// Refactored from https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/92866a25d86943454624c5ac4fe38a59a17ed7d1/help-center/packages/util/fetch/fetch.tsx#fetch.tsx-51:66
const NETWORK_ERROR_CODE = 0;
const EMPTY_TRACE_ID_ERROR_MESSAGE = 'Failed to fetch got empty traceId';

const validateResponse = (_request: RequestParameters, response: GraphQLSingularResponse) => {
    if ('errors' in response === true) {
        const traceId = response.extensions?.gateway?.request_id;
        const statusCode = getErrorStatusCode(response.errors);

        if (typeof traceId === 'undefined' && isFilterNullTraceIdFromGraphqlCallsEnabled()) {
            throw new FetchError(EMPTY_TRACE_ID_ERROR_MESSAGE, NETWORK_ERROR_CODE);
        }

        if (traceId) {
            throw new FetchError(`ErrorCode: ${statusCode}, traceId: ${traceId}`, statusCode, true);
        }
    }

    // This function returns the response but this unused.
    // All that we rely upon is thrown errors to ensure that there aren't any problems with the response.
    // eslint-disable-next-line eslint/no-void
    void handleGraphQlErrors(response);

    // This returns true as the response will be valid if an error is not thrown above.
    return true;
};

type AggErrorExtension = {
    allowedAuth?: string[];
    presentedAuth?: string;
    errorSource: 'GRAPHQL_GATEWAY' | 'UNDERLYING_SERVICE';
    statusCode: number;
    classification: 'InvalidSyntax' | 'IncorrectAuthException' | 'ServiceHostUnknownError' | string;
};

type AggError = PayloadError & {
    extensions?: AggErrorExtension;
};

function errorHasAggErrorExtension(err: unknown): err is AggErrorExtension {
    if (typeof err !== 'object' || err === null) return false;
    if ('extensions' in err === false || typeof err !== 'object' || err === null) return false;
    if (typeof err.extensions !== 'object' || err.extensions === null || 'statusCode' in err.extensions === false)
        return false;
    return typeof err.extensions.statusCode === 'number';
}

function getErrorStatusCode(errors: undefined | AggError[]): number {
    if (Array.isArray(errors) === false) {
        return 200;
    }

    const errorExtension = errors.find(errorHasAggErrorExtension);

    if (typeof errorExtension?.extensions?.statusCode !== 'number') {
        return 500;
    }

    return errorExtension.extensions.statusCode;
}

export default {
    setEndpoint,
    getHeaders,
    validateResponse,
    getErrorStatusCode,
    errorHasAggErrorExtension,
};
